import React, { useState } from 'react'

import { EDataMode, EAnalysisStatus } from 'types/Enums'
import { SxProps, useTheme } from '@mui/material'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { useAnalysis, useReportMetric } from 'redux/hooks/Analyses'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Checkbox from 'components_new/atoms/Checkbox'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import BarChart from 'components_new/molecules/charts/BarChart'
import BubbleChart from 'components_new/molecules/charts/BubbleChart'
import ContentBox from 'components_new/molecules/ContentBox'
import DistributionBar from 'components_new/molecules/DistributionBar'
import GenericAvatar from 'components_new/molecules/GenericAvatar'
import IconButton from 'components_new/molecules/IconButton'
import LineChart from 'components_new/molecules/charts/LineChart'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import ValueText from 'components_new/molecules/ValueText'
import SearchField from 'components_new/molecules/SearchField'
import SidebarDialog from 'components_new/molecules/SidebarDialog'
import SimpleTable from 'components_new/molecules/SimpleTable'

import AnalysisCalculation from 'components_new/organisms/analyses/AnalysisCalculation'
import AnalysisTrend from 'components_new/organisms/analyses/AnalysisTrend'
import DataStatus from 'components_new/organisms/DataStatus'

import { Highlighted } from '../../StandardSearch/utils'

import { INACTIVE_SX_PROPS } from 'themes'

import {
  formatValue,
  getColor,
  getPrefixValue
} from 'components_new/organisms/Widget/Types/utils'

import type {
  TAnalysisClassification,
  TAnalysisMetaDto,
  TAnalysisPropertyDto
} from '@homepal/api-contract'

import { AUTO_SIGNIFICANT_FIGURES, getAutoScale } from 'utils/autoFormat'
import { getRecommendationArgument } from 'utils/recommendation'

interface AnalysisSelectionProps {
  id: string

  /** Excludes the property from the analysis type and reruns this one. */
  onExclude?: (name: string) => void
  onExplore?: (name: string) => void
  sx?: SxProps
}

const AnalysisSelection = (props: AnalysisSelectionProps) => {
  const { id, onExclude, onExplore, sx } = props

  const { lang, t } = useTranslation()
  const { tryUpdateAnalysisSelection } = useAnalysisActions()
  const theme = useTheme()

  const { analysis } = useAnalysis(id)
  const metric = useReportMetric(analysis)

  const locked = analysis?.status !== EAnalysisStatus.UNHANDLED

  // Every property is plotted; only the qualified ones can be selected.
  const properties = analysis?.properties ?? []
  const selections = properties.filter(
    (property) => property.classification !== 'not_qualified'
  )
  const empty = selections.length === 0

  // Potential only below the noise: nothing to recommend or argue for.
  const belowNoise =
    !empty &&
    selections.every(
      (selection) => selection.classification === 'not_prioritized'
    )

  const [trendDialog, setTrendDialog] = useState<TAnalysisPropertyDto | null>(
    null
  )
  const [searchQuery, setSearchQuery] = useState('')

  const filteredSelections = selections.filter((selection) =>
    selection.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleToggle = (value: string) => {
    const object = selections.find((selection) => selection.name === value)

    if (!object) return

    const selectedNames = selections
      .filter((selection) => selection.selected)
      .map((selection) => selection.name)

    const nextNames = object.selected
      ? selectedNames.filter((name) => name !== value)
      : [...selectedNames, value]

    tryUpdateAnalysisSelection(id, nextNames)
  }

  // The same choices on a bubble and on a row. Excluding reruns the
  // analysis, so like the selection it is only for an unhandled one.
  const getPropertyOptions = (name: string): OptionProps[] => {
    const selection = selections.find((item) => item.name === name)
    const excludable = Boolean(onExclude) && !locked

    const options: OptionProps[] = [
      ...(onExplore
        ? [
            {
              body: t('analyses', 'explore_body'),
              iconName: 'Explore' as const,
              onClick: () => onExplore(name),
              title: t('global', 'explore')
            }
          ]
        : []),
      ...(selection && !locked
        ? [
            {
              body: t('analyses', 'selection_body'),
              iconName: selection.selected
                ? ('CheckBoxOutlineBlank' as const)
                : ('CheckBox' as const),
              onClick: () => handleToggle(name),
              title: selection.selected
                ? t('analyses', 'remove_from_selection')
                : t('analyses', 'add_to_selection')
            }
          ]
        : [])
    ]

    if (!excludable) return options

    return [
      ...options.map((option, index) =>
        index === options.length - 1 ? { ...option, divider: true } : option
      ),
      {
        body: t('analyses', 'exclude_and_rerun_body'),
        iconName: 'RemoveCircleOutlineOutlined',
        onClick: () => onExclude?.(name),
        title: t('analyses', 'exclude_and_rerun')
      }
    ]
  }

  if (!analysis) return null

  const unit = metric?.unit || null
  const measuresTrend = analysis.meta?.metric?.measures === 'trend'

  // one unit for the metric values in the chart and the table, e.g. mkr
  const { unitPrefix, unitLabel } = getAutoScale(
    [
      ...selections.flatMap((item) => [item.start_value, item.target_value]),
      ...(measuresTrend ? [] : properties.map((item) => item.position))
    ],
    unit,
    metric?.unit_prefixes
  )
  const scale = (value: number | null | undefined) =>
    typeof value === 'number' ? getPrefixValue(value, unitPrefix) : null
  // a trend is placed by its deviation in %, which keeps its own unit
  const scalePosition = (value: number | null | undefined) =>
    measuresTrend ? value ?? null : scale(value)

  const positionUnit = measuresTrend ? '%' : unitLabel
  const positionLabel = measuresTrend
    ? t('analyses', 'deviation_from_target')
    : metric?.title ?? ''
  const recommendationMode = analysis.meta?.recommendation_mode ?? null
  const argument = belowNoise
    ? t('analyses', 'recommendation_below_noise')
    : getRecommendationArgument(selections, recommendationMode, t, lang)
  const preselected = recommendationMode !== 'flat' && !belowNoise
  const referenceLines = analysis.meta?.reference_lines ?? null

  // Only some analyses carry a context; it is shown in the table alone.
  const contextLabel = analysis.meta?.context_label ?? null
  const contextColors = getContextColors(properties, (index) =>
    getColor(null, index, theme, true, null, null, 1, true)
  )

  return (
    <>
      <Box sx={sx}>
        <ContentBox
          collapse={{ active: !empty }}
          color="brand"
          header={{
            icon: <GenericAvatar size="small" variant="homepal" />,
            title: 'Homepal AI',
            titleAdornment: (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {t(
                  'analyses',
                  empty
                    ? 'no_recommended_selection'
                    : belowNoise
                      ? 'no_selection_above_noise'
                      : preselected
                        ? 'recommended_selection'
                        : 'no_preselection'
                )}
              </Box>
            )
          }}
          sx={{
            mb: 2
          }}
        >
          {empty ? null : (
            <Box sx={{ maxWidth: 'content' }}>
              <SimpleTable
                border={true}
                rows={[
                  ...(preselected
                    ? [
                        {
                          cells: [
                            <Text key={'units_title'}>
                              {t('org_units', 'units')}
                            </Text>,
                            <Box
                              key={'units_content'}
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5
                              }}
                            >
                              {selections
                                .filter(
                                  (item) =>
                                    item.classification === 'recommended'
                                )
                                .map((item) => (
                                  <Chip
                                    key={item.name}
                                    label={item.name}
                                    size="small"
                                  />
                                ))}
                            </Box>
                          ]
                        }
                      ]
                    : []),
                  ...(argument
                    ? [
                        {
                          cells: [
                            <Text key={'reasoning_title'}>
                              {t('analyses', 'reasoning')}
                            </Text>,
                            <Text key={'reasoning_content'}>{argument}</Text>
                          ]
                        }
                      ]
                    : [])
                ]}
              />
            </Box>
          )}
        </ContentBox>

        <Box sx={{ height: 400, mb: 2 }}>
          <BubbleChart
            data={{
              datasets: [
                {
                  color: properties.map((item) =>
                    item.selected
                      ? theme.palette.primary.main
                      : 'rgba(0,0,0,0.25)'
                  ),
                  label: positionLabel,
                  type: 'bubble',
                  unit: positionUnit,
                  values: properties.map((item) => scalePosition(item.position))
                },
                {
                  label: t('analyses', 'report_col_potential'),
                  type: 'bubble',
                  unit: 'kr/år',
                  values: properties.map((item) => item.noi_change)
                }
              ],
              horizontalLines: getLines([
                {
                  label: t('analyses', 'recommendation_limit'),
                  value: referenceLines?.recommendation
                }
              ]),
              labels: properties.map((item) => item.name),
              verticalLines: getLines([
                {
                  label: t('analyses', 'report_target_value'),
                  value: scalePosition(referenceLines?.target)
                },
                {
                  label: t('analyses', 'noise'),
                  value: scalePosition(referenceLines?.noise)
                }
              ])
            }}
            showLabel={(_, index) => Boolean(properties[index]?.selected)}
            options={(clicked) => getPropertyOptions(clicked.label)}
            significantFigures={AUTO_SIGNIFICANT_FIGURES}
            variant="detailed"
          />
        </Box>

        {empty ? null : (
          <>
            {/*-- search --*/}
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
              <SearchField
                onClear={() => setSearchQuery('')}
                onSearch={(value) => setSearchQuery(value)}
                placeholder={t('global', 'search')}
                size="small"
                value={searchQuery}
              />
            </Box>

            {/*-- table --*/}
            <SimpleTable
              columns={[
                { title: t('org_units', 'unit') },
                {
                  defaultSort: 'desc',
                  title: t('analyses', 'classification')
                },
                { title: t('global', 'trend') },
                ...(contextLabel ? [{ title: contextLabel }] : []),
                { title: '' },
                { align: 'right', title: t('analyses', 'potential') },
                { align: 'right', title: t('analyses', 'report_start_value') },
                { align: 'right', title: t('analyses', 'report_target_value') },
                { title: '' }
              ]}
              hover={true}
              maxHeight={400}
              sx={{ height: 400 }}
              rows={filteredSelections.map((selection) => {
                const rowOptions = getPropertyOptions(selection.name)

                return {
                  cells: [
                    <Box
                      key="segment"
                      sx={{ alignItems: 'center', display: 'flex', gap: 0.25 }}
                    >
                      <Checkbox
                        checked={selection.selected}
                        disabled={locked}
                        onChange={() => handleToggle(selection.name)}
                        size="small"
                      />
                      <Text>
                        <Highlighted
                          highlight={searchQuery}
                          text={selection.name}
                        />
                      </Text>
                    </Box>,
                    <>
                      {selection.classification !== 'not_qualified' ? (
                        <Tooltip
                          title={t(
                            'analyses',
                            `classification_${selection.classification}_tooltip`
                          )}
                        >
                          <Chip
                            label={t(
                              'analyses',
                              `classification_${selection.classification}`
                            )}
                            size="xsmall"
                          />
                        </Tooltip>
                      ) : undefined}
                    </>,
                    <Box
                      key="trend"
                      onClick={(e) => {
                        e.stopPropagation()
                        setTrendDialog(selection)
                      }}
                      sx={{
                        height: 24,
                        width: 96,
                        ['&:hover']: {
                          bgcolor: 'action.hover',
                          cursor: 'zoom-in'
                        }
                      }}
                    >
                      <LineChart
                        data={{
                          labels: selection.series.map(
                            (point) => point.segment
                          ),
                          isTimeseries: true,
                          datasets: [
                            {
                              color: theme.palette.brand.main,
                              label: t('analyses', 'trend_current_period'),
                              type: 'line',
                              unit,
                              values: selection.series.map(
                                (point) => point.value
                              )
                            },
                            {
                              label: t('analyses', 'trend_previous_period'),
                              type: 'line',
                              unit,
                              values: selection.series.map(
                                (point) => point.comparative_value
                              )
                            }
                          ]
                        }}
                        variant="minimal"
                      />
                    </Box>,
                    ...(contextLabel && analysis.meta
                      ? [
                          <ContextCell
                            colors={contextColors}
                            heading={`${selection.name} · ${contextLabel}`}
                            key="context"
                            meta={analysis.meta}
                            property={selection}
                          />
                        ]
                      : []),
                    <>
                      {onExplore ? (
                        <IconButton
                          key="explore"
                          name="Explore"
                          onClick={() => onExplore(selection.name)}
                          size="small"
                          title={t('global', 'explore')}
                        />
                      ) : undefined}
                    </>,
                    <Box
                      key="potential"
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 0.5,
                        justifyContent: 'flex-end'
                      }}
                    >
                      <ValueText
                        align="right"
                        color="success.text"
                        displayPositiveIndicator={true}
                        unit="kr/år"
                        value={selection.noi_change}
                      />
                      <AnalysisCalculation
                        calculation={selection.calculation}
                        potential={selection.noi_change}
                        result={analysis.meta?.result ?? null}
                      />
                    </Box>,
                    <ValueText
                      align="right"
                      key="start_value"
                      unit={unitLabel}
                      value={scale(selection.start_value)}
                    />,
                    <ValueText
                      align="right"
                      key="target_value"
                      unit={unitLabel}
                      value={scale(selection.target_value)}
                    />,
                    <>
                      {rowOptions.length > 0 ? (
                        <OptionsMenuButton
                          key="options"
                          options={rowOptions}
                          size="small"
                        />
                      ) : undefined}
                    </>
                  ],
                  id: selection.name,
                  sortValues: [
                    { value: selection.name },
                    {
                      value: CLASSIFICATION_RANK[selection.classification],
                      tiebreak: selection.noi_change ?? 0
                    },
                    { value: undefined },
                    ...(contextLabel
                      ? [
                          {
                            value:
                              analysis.meta?.context_type === 'value'
                                ? selection.context[0]?.value ?? undefined
                                : undefined
                          }
                        ]
                      : []),
                    { value: undefined },
                    { value: selection.noi_change ?? 0 },
                    { value: selection.start_value ?? undefined },
                    { value: selection.target_value ?? undefined },
                    { value: undefined }
                  ],
                  sx: !selection.selected ? INACTIVE_SX_PROPS : undefined
                }
              })}
              size="small"
            />
          </>
        )}
      </Box>

      {/*-- dialogs --*/}
      {analysis.metric ? (
        <SidebarDialog
          actions={
            <Button onClick={() => setTrendDialog(null)} variant="outlined">
              {t('global', 'close')}
            </Button>
          }
          open={Boolean(trendDialog)}
          onClose={() => setTrendDialog(null)}
          title={t('global', 'trend')}
          titleActions={
            <DataStatus
              date={analysis.created_at}
              mode={EDataMode.LOCKED}
              variant="chip"
            />
          }
        >
          {trendDialog ? (
            <AnalysisTrend
              calculation={trendDialog.calculation}
              comparativeValues={trendDialog.series.map(
                (point) => point.comparative_value
              )}
              id={id}
              orgUnit={trendDialog.name}
              potential={trendDialog.noi_change ?? 0}
              result={analysis.meta?.result ?? null}
              segments={trendDialog.series.map((point) => point.segment)}
              startValue={trendDialog.start_value}
              targetValue={trendDialog.target_value}
              values={trendDialog.series.map((point) => point.value)}
            />
          ) : null}
        </SidebarDialog>
      ) : null}
    </>
  )
}

export default AnalysisSelection

/** The classification column: the recommended first, then by potential. */
const CLASSIFICATION_RANK: Record<TAnalysisClassification, number> = {
  recommended: 3,
  potential: 2,
  not_prioritized: 1,
  not_qualified: 0
}

/** A null line is not drawn: absent on analyses generated before it. */
function getLines(
  lines: { label: string; value: number | null | undefined }[]
): { label: string; value: number }[] {
  return lines.flatMap((line) =>
    typeof line.value === 'number'
      ? [{ label: line.label, value: line.value }]
      : []
  )
}

/**
 * Colour follows the label, not the position; the largest share leads.
 * The share without a category takes no palette colour, see ContextCell.
 */
function getContextColors(
  properties: TAnalysisPropertyDto[],
  colorAt: (index: number) => string
): Record<string, string> {
  const totals = properties
    .flatMap((property) => property.context)
    .reduce<Record<string, number>>((acc, entry) => {
      if (entry.label === null) {
        return acc
      }

      const label = entry.label ?? ''

      acc[label] = (acc[label] ?? 0) + (entry.value ?? 0)

      return acc
    }, {})

  return Object.fromEntries(
    Object.keys(totals)
      .sort((a, b) => totals[b] - totals[a])
      .map((label, index) => [label, colorAt(index)])
  )
}

/**
 * A single figure, labelled figures as a bar chart, or the shares as one
 * bar —
 * `meta.context_type`.
 */
const ContextCell = (props: {
  colors: Record<string, string>
  heading: string
  meta: TAnalysisMetaDto
  property: TAnalysisPropertyDto
}) => {
  const { colors, heading, meta, property } = props
  const { t } = useTranslation()
  const theme = useTheme()

  // Null or left out is a value the data lacks, shown as in a dashboard.
  const missing = t('global', 'missing_value')

  // A figure with the precision and unit the analysis draws it with.
  const withUnit = (value: number) =>
    `${formatValue(value, null, meta.context_decimals ?? 0)}${
      meta.context_unit ? ` ${meta.context_unit}` : ''
    }`

  if (meta.context_type === 'value') {
    const value = property.context[0]?.value

    if (typeof value !== 'number') {
      return <Text color="text.secondary">{missing}</Text>
    }

    return <Text>{withUnit(value)}</Text>
  }

  if (property.context.length === 0) {
    return <Text color="text.secondary">{missing}</Text>
  }

  if (meta.context_type === 'bar') {
    return (
      <Box sx={{ height: 24, width: 96 }}>
        <BarChart
          beginYAtZero
          compact
          data={{
            labels: property.context.map((entry) => entry.label ?? ''),
            datasets: [
              {
                color: theme.palette.brand.main,
                label: heading,
                type: 'bar',
                unit: meta.context_unit ?? null,
                values: property.context.map((entry) => entry.value)
              }
            ]
          }}
          decimals={meta.context_decimals ?? 0}
          missingStub
          tooltipStyle={{
            background: theme.palette.background.paper,
            backdropFilter: 'none',
            zIndex: String(theme.zIndex.tooltip)
          }}
          variant="minimal"
          yRange={{ clamp: true, max: 100, min: 0 }}
        />
      </Box>
    )
  }

  return (
    <DistributionBar
      heading={heading}
      segments={property.context.map((entry) =>
        entry.label === null
          ? {
              color: theme.palette.grey[400],
              label: missing,
              value: entry.value ?? 0
            }
          : {
              color: colors[entry.label ?? ''],
              label: entry.label ?? '',
              value: entry.value ?? 0
            }
      )}
    />
  )
}
